import { useState, useCallback } from "react";

import type { CheckoutSession, AppContext, StoredCard } from "@/types";
import { PaymentMethod } from "@/types";
import * as lib3ds from "@/lib/3ds";
import { parseCardDate } from "@/lib/card";
import { newLocalStorageStore } from "@/lib/store";
import {
  handleHttpError,
  type ApiError,
} from "@/lib/http-error-recovery";
import { useCosmosClient } from "@/services/cosmos";
import type { TokenizeCardResult } from "@/services/cosmos";
import { CksCheckoutStateSchema } from "@/services/cosmos/schema/checkout-service";
import { logInfo } from "@/services/logging/logger";
import { emitPaymentSubmissionTracking } from "@/shared/tracking/analytics";

import {
  PROCESSING,
  type Processing,
  type PaymentSubmissionOptions,
} from "../types";
import * as submissionService from "../services/payment-submission-service";
import { calculateAmountToPay } from "../services/payment-method-utils";

const CARD_ID_NEW = "new";

function isLocalNonHttpError(err: unknown): boolean {
  const apiErr = err as { response?: unknown; request?: unknown };
  return apiErr?.response === undefined && apiErr?.request === undefined;
}

async function attemptRecovery(
  err: unknown,
  refetchSession: () => Promise<CheckoutSession>,
  currentSession: CheckoutSession | undefined,
  onSessionFetched: (session: CheckoutSession) => void,
  endpoint: string,
  httpMethod: string,
  requestContext: Record<string, unknown>,
): Promise<boolean> {
  if (isLocalNonHttpError(err)) {
    return false;
  }

  const errorSession = (err as { session?: CheckoutSession }).session;
  if (errorSession) {
    onSessionFetched(errorSession);
    return true;
  }

  try {
    const recoveryResult = await handleHttpError(err as ApiError, {
      refetchSession,
      currentSession,
      endpoint,
      httpMethod,
      requestContext,
    });

    if (recoveryResult.recovered) {
      logInfo(
        "usePaymentSubmission",
        "Recovered from payment error via session refetch",
        {
          recoveredStatus: recoveryResult.session.status,
          originalError: (err as ApiError).response?.status,
        },
      );
      onSessionFetched(recoveryResult.session);
      return true;
    }

    onSessionFetched(recoveryResult.session);
    return false;
  } catch {
    return false;
  }
}

/**
 * Orchestrates payment submission flows with HTTP error recovery aligned to main:
 * Apple Pay cancellations and similar local errors skip handleHttpError so the
 * user stays on-screen and can retry.
 */
export function usePaymentSubmission(
  session: CheckoutSession | undefined,
  onSessionFetched: (session: CheckoutSession) => void,
) {
  const client = useCosmosClient();
  const [processing, setProcessing] = useState<Processing>(PROCESSING.NONE);

  const refetchSession = useCallback(async () => {
    const freshData = await client.getCheckoutSessionV3();
    return CksCheckoutStateSchema.parse(freshData);
  }, [client]);

  const submitWithExistingCard = useCallback(
    async (
      cardId: string,
      options: PaymentSubmissionOptions,
      cvv?: string,
      fingerprint?: string,
      card?: StoredCard,
    ): Promise<void> => {
      if (!session) return;

      try {
        setProcessing(PROCESSING.SUBMIT);

        emitPaymentSubmissionTracking("payment_submission_started", session, {
          payment_method_type: "card",
          card_id: cardId !== CARD_ID_NEW ? cardId : undefined,
          wallet_selected: options.shouldUseWalletBalance ?? false,
        });

        const newSession = await submissionService.payWithExistingCard(
          client,
          session,
          cardId,
          options,
          cvv,
          fingerprint,
          card,
        );

        onSessionFetched(newSession);
        setProcessing(PROCESSING.NONE);
      } catch (err) {
        emitPaymentSubmissionTracking("payment_submission_failed", session, {
          payment_method_type: "card",
          card_id: cardId !== CARD_ID_NEW ? cardId : undefined,
          wallet_selected: options.shouldUseWalletBalance ?? false,
          error_message: err instanceof Error ? err.message : String(err),
        });

        await attemptRecovery(
          err,
          refetchSession,
          session,
          onSessionFetched,
          "select-payment-method",
          "POST",
          { paymentMethod: "CREDIT_CARD", cardId },
        );

        setProcessing(PROCESSING.NONE);
        throw err;
      }
    },
    [session, client, onSessionFetched, refetchSession],
  );

  const submitWithApplePay = useCallback(
    async (
      ctx: AppContext,
      isWalletSelected: boolean,
      options: PaymentSubmissionOptions,
    ): Promise<void> => {
      if (!session) return;

      try {
        setProcessing(PROCESSING.SUBMIT);

        const amountToPay = calculateAmountToPay(ctx, isWalletSelected);

        emitPaymentSubmissionTracking("payment_submission_started", session, {
          payment_method_type: "apple_pay",
          wallet_selected: isWalletSelected,
        });

        const newSession = await submissionService.payWithApple(
          client,
          ctx,
          window,
          isWalletSelected,
          options,
          amountToPay,
        );

        onSessionFetched(newSession);
        setProcessing(PROCESSING.NONE);
      } catch (err) {
        emitPaymentSubmissionTracking("payment_submission_failed", session, {
          payment_method_type: "apple_pay",
          wallet_selected: isWalletSelected,
          error_message: err instanceof Error ? err.message : String(err),
        });

        await attemptRecovery(
          err,
          refetchSession,
          session,
          onSessionFetched,
          "select-payment-method",
          "POST",
          { paymentMethod: "APPLE_PAY" },
        );

        setProcessing(PROCESSING.NONE);
        throw err;
      }
    },
    [session, client, onSessionFetched, refetchSession],
  );

  const submitWithWalletOnly = useCallback(
    async (options: PaymentSubmissionOptions): Promise<void> => {
      if (!session) return;

      try {
        setProcessing(PROCESSING.SUBMIT);

        emitPaymentSubmissionTracking("payment_submission_started", session, {
          payment_method_type: "wallet_only",
          wallet_selected: true,
        });

        const newSession = await submissionService.payWithWalletOnly(
          client,
          session,
          options,
        );

        onSessionFetched(newSession);
        setProcessing(PROCESSING.NONE);
      } catch (err) {
        emitPaymentSubmissionTracking("payment_submission_failed", session, {
          payment_method_type: "wallet_only",
          wallet_selected: true,
          error_message: err instanceof Error ? err.message : String(err),
        });

        await attemptRecovery(
          err,
          refetchSession,
          session,
          onSessionFetched,
          "select-payment-method",
          "POST",
          { paymentMethod: "CREDIT_CARD", isWalletOnly: true },
        );

        setProcessing(PROCESSING.NONE);
        throw err;
      }
    },
    [session, client, onSessionFetched, refetchSession],
  );

  const confirmPnmOrder = useCallback(
    async (options: PaymentSubmissionOptions): Promise<void> => {
      if (!session) return;

      try {
        setProcessing(PROCESSING.SUBMIT);

        emitPaymentSubmissionTracking("payment_submission_started", session, {
          payment_method_type: "pnm_confirm",
        });

        const newSession = await submissionService.confirmPnmOrder(
          client,
          session,
          options,
        );

        onSessionFetched(newSession);
        setProcessing(PROCESSING.NONE);
      } catch (err) {
        emitPaymentSubmissionTracking("payment_submission_failed", session, {
          payment_method_type: "pnm_confirm",
          error_message: err instanceof Error ? err.message : String(err),
        });

        await attemptRecovery(
          err,
          refetchSession,
          session,
          onSessionFetched,
          "select-payment-method",
          "POST",
          { paymentMethod: "PNM_CONFIRM" },
        );

        setProcessing(PROCESSING.NONE);
        throw err;
      }
    },
    [session, client, onSessionFetched, refetchSession],
  );

  const tokenizeAndSubmitCard = useCallback(
    async (
      cardData: { cardNumber: string; expiryDate: string; cvv: string },
      options: PaymentSubmissionOptions,
      customerId: string,
      onTokenizeError: (error: Error) => void,
    ): Promise<TokenizeCardResult> => {
      if (!session) {
        throw new Error("Session not available");
      }

      const store = newLocalStorageStore(window, session.id);
      await lib3ds.clear3dsState(store);
      lib3ds.clearSessionFlags(client.config().checkoutId);
      logInfo("payment-submission", "Cleared 3DS state for new card", {
        sessionId: session.id,
      });

      try {
        setProcessing(PROCESSING.TOKENIZATION);

        const { month, year } = parseCardDate(cardData.expiryDate);

        const result = await client.tokenizeCard(customerId, {
          number: cardData.cardNumber.replace(/ /g, ""),
          expiryMonth: month,
          expiryYear: year,
          cvv: cardData.cvv,
        });

        setProcessing(PROCESSING.SUBMIT);

        emitPaymentSubmissionTracking("payment_submission_started", session, {
          payment_method_type: "card",
          card_id: CARD_ID_NEW,
          wallet_selected: options.shouldUseWalletBalance ?? false,
        });

        const newSession = await client.selectPaymentMethod(
          PaymentMethod.CREDIT_CARD,
          {
            token: result.token,
            termsAccepted: options.termsAccepted,
            documentsSigned: options.documentsSigned,
            shouldUseWalletBalance: options.shouldUseWalletBalance,
            fingerprint: result.fingerprint,
            ...(result.bin !== undefined && { cardBin: result.bin }),
            ...(result.last4 !== undefined && { cardLast4: result.last4 }),
            cardExpiryMonth: String(result.expiry_month ?? month),
            cardExpiryYear: String(result.expiry_year ?? year),
          },
        );

        emitPaymentSubmissionTracking(
          "payment_submission_completed",
          session,
          {
            payment_method_type: "card",
            card_id: CARD_ID_NEW,
            wallet_selected: options.shouldUseWalletBalance ?? false,
          },
        );

        onSessionFetched(newSession);
        setProcessing(PROCESSING.NONE);

        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        onTokenizeError(error);

        emitPaymentSubmissionTracking("payment_submission_failed", session, {
          payment_method_type: "card",
          card_id: CARD_ID_NEW,
          wallet_selected: options.shouldUseWalletBalance ?? false,
          error_message: error.message,
        });

        await attemptRecovery(
          err,
          refetchSession,
          session,
          onSessionFetched,
          "select-payment-method",
          "POST",
          { paymentMethod: "CREDIT_CARD", cardId: CARD_ID_NEW },
        );

        setProcessing(PROCESSING.NONE);
        throw err;
      }
    },
    [session, client, onSessionFetched, refetchSession],
  );

  return {
    processing,
    submitWithExistingCard,
    submitWithApplePay,
    submitWithWalletOnly,
    confirmPnmOrder,
    tokenizeAndSubmitCard,
  };
}
